import { BRANDS, getBrand, isBrandId, type BrandConfig } from "../services/ai/brandRegistry";
import { logBrandingFlagDiag } from "../../shared/featureFlags/brandingFlagDiag";

export { AUTH_EXPIRED_EVENT, checkAuthStatus, notifyAuthExpired } from "../../shared/utils/authStatus";

export interface ResolveActiveBrandInput {
  companyId?: string | null;
  /** Brand id served by the single `pptxTemplateBrand` ConfigCat text setting. */
  activeBrandTemplateId?: string | null;
}

/**
 * Resolve which company brand (if any) should be offered to the current
 * user. A user is ever offered AT MOST ONE brand toggle.
 *
 * Resolution relies only on authoritative auth data (FactSet CompanyId) and
 * the single `pptxTemplateBrand` ConfigCat text setting — never on email
 * string matching.
 *
 * Precedence (auth takes precedence over the ConfigCat flag):
 *   1. FactSet CompanyId (auth payload) matches a brand's companyId.
 *   2. `activeBrandTemplateId` resolves to a known brand id.
 *   3. Otherwise `null` — no toggle is shown.
 */
export function resolveActiveBrand(input: ResolveActiveBrandInput): BrandConfig | null {
  const companyId = input.companyId?.toString().trim();

  if (companyId) {
    const byCompany = BRANDS.find((brand) => brand.companyId === companyId);
    if (byCompany) return byCompany;
  }

  const templateId = input.activeBrandTemplateId?.toString().trim();
  if (templateId && isBrandId(templateId)) {
    return getBrand(templateId) ?? null;
  }

  return null;
}

/** Diagnostic snapshot for local branding-flag debugging (see `brandingFlagDiag.ts`). */
export function explainBrandResolution(input: ResolveActiveBrandInput): Record<string, unknown> {
  const companyId = input.companyId?.toString().trim() || null;
  const activeBrandTemplateId = input.activeBrandTemplateId?.toString().trim() || null;
  const resolved = resolveActiveBrand(input);

  let resolvedVia: "companyId" | "featureFlag" | "none" = "none";
  if (companyId) {
    const byCompany = BRANDS.find((brand) => brand.companyId === companyId);
    if (byCompany && resolved?.id === byCompany.id) {
      resolvedVia = "companyId";
    }
  }
  if (resolvedVia === "none" && resolved) {
    resolvedVia = "featureFlag";
  }

  const payload = {
    companyId,
    activeBrandTemplateId,
    resolvedBrandId: resolved?.id ?? null,
    resolvedVia,
    registryOrder: BRANDS.map((brand) => brand.id),
  };

  logBrandingFlagDiag("brandResolution", payload);
  return payload;
}

export { parseErrorBodyText, parseErrorResponse, readApiError } from "../../shared/utils/apiError";
